const STATUS_META: Record<string, { color: string; label: string }> = {
  ok: { color: "var(--status-good)", label: "OK" },
  partial: { color: "var(--status-warning)", label: "WARN" },
  error: { color: "var(--status-critical)", label: "FAIL" },
  // Not "the poll failed" but "nothing has reported in long enough that the
  // recorded status is no longer evidence of anything". Its own colour so it
  // cannot be mistaken for either a healthy device or a failing one.
  stale: { color: "var(--status-serious)", label: "STALE" },
  never: { color: "var(--text-muted)", label: "-----" },
  // Planned work: polled, alerting nobody. Its own colour, distinct from every
  // health state, because it says nothing about health -- only that a person
  // is working on it and the alerts are deliberately off.
  maint: { color: "var(--status-maint)", label: "MAINT" },
  // Deliberately switched off. Neutral grey, outside the phosphor greens, so
  // it reads as inert rather than as either healthy or failing.
  off: { color: "var(--status-off)", label: "OFF" },
};

/** Widest label, so the bracketed codes stay in a column. */
export const LABEL_WIDTH = 5;

/**
 * The badge's colour and its padded label.
 *
 * Separated from the component so the padding can be tested: the brackets
 * sitting still is the entire point of the badge, and it is the sort of thing
 * that breaks silently and invisibly on a machine with no browser.
 */
export function statusMeta(status: string): { color: string; label: string } {
  const meta = STATUS_META[status] ?? { color: "var(--text-muted)", label: status.toUpperCase() };
  // Deliberately pads but never truncates. An unrecognised status is a bug
  // somewhere else, and silently clipping it to five characters would hide
  // the evidence to keep a column tidy.
  return { color: meta.color, label: meta.label.padEnd(LABEL_WIDTH, " ") };
}

/**
 * Status as a bracketed code, not a coloured dot.
 *
 * The label alone carries the meaning, so this survives greyscale, a
 * colour-blind reader, and forced-colors mode -- colour is only ever
 * reinforcement here, never the signal.
 *
 * `pre` rather than `nowrap`, and that is load-bearing. The label is padded to
 * a fixed width so the brackets read as a fixed indicator lamp rather than
 * jumping about as the status changes, but `nowrap` collapses a run of spaces
 * exactly as `normal` does -- it only suppresses line breaking. The padding
 * was being written and then thrown away by the renderer. Every character in
 * this font stack has the same advance, including the pad, so the brackets
 * line up by construction.
 */
export function StatusBadge({ status }: { status: string }) {
  const meta = statusMeta(status);
  return (
    <span
      style={{
        color: meta.color,
        fontSize: 13,
        letterSpacing: "0.08em",
        whiteSpace: "pre",
      }}
    >
      [{meta.label}]
    </span>
  );
}
